import { useEffect, useRef, useState } from "react";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";

/**
 * Branded-splash orchestrator. Owns the INITIAL boot sequence so the splash shows
 * REAL staged progress (each stage kicks off real work), never flashes (a ~2.2s
 * floor), and can't hang on a slow non-critical fetch (per-call timeout + a stall
 * retry). App renders <Boot/> until `done`, then the app.
 *
 * Stages ↔ real work:
 *   0 آماده‌سازی      — SDK/viewport already up (main.tsx); begin.
 *   1 اتصال…          — loadProfile(): the auth + getMe handshake (critical path).
 *   2 دریافت اطلاعات — refreshFriends + refreshDmUnread, STAGGERED (no boot herd).
 *   3 تقریباً آماده   — settle; reveal once profile is ready AND the floor elapsed.
 */
const MIN_MS = 2200; // deliberate branded beat — don't flash the splash
const STAGE_MS = 520; // dwell per caption
const SECONDARY_TIMEOUT_MS = 2500; // a slow friends/dm fetch can't hold boot
const STALL_MS = 8000; // getMe still not ready → one retry + a "taking a while" note

export interface BootView {
  done: boolean;
  caption: string;
  progress: number; // 0..1, drives the deterministic bar in <Boot/>
}

const STAGE_CAPTIONS = [
  fa.boot.preparing,
  fa.boot.connecting,
  fa.boot.fetching,
  fa.boot.almost,
] as const;

export function useBootSequence(): BootView {
  const status = useSession((s) => s.status);
  const loadProfile = useSession((s) => s.loadProfile);
  const refreshFriends = useSession((s) => s.refreshFriends);
  const refreshDmUnread = useSession((s) => s.refreshDmUnread);

  const [stage, setStage] = useState(0);
  const [minElapsed, setMinElapsed] = useState(false);
  const [stalled, setStalled] = useState(false);
  const started = useRef(false);
  // Live status for timer callbacks (their closure captures the mount-time value).
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    if (started.current) return; // run the sequence exactly once (StrictMode-safe)
    started.current = true;
    const timers: number[] = [];
    const later = (fn: () => void, ms: number): void => {
      timers.push(window.setTimeout(fn, ms));
    };

    // Stage 1 — the critical auth/profile handshake.
    later(() => setStage(1), STAGE_MS);
    void loadProfile();

    // Stage 2 — fetch the rest of the session data, staggered.
    later(() => {
      setStage(2);
      void withTimeout(refreshFriends(), SECONDARY_TIMEOUT_MS);
      later(() => void withTimeout(refreshDmUnread(), SECONDARY_TIMEOUT_MS), 260);
    }, STAGE_MS * 2);

    // Stage 3 — settling.
    later(() => setStage(3), STAGE_MS * 3);

    // Min-duration floor + the stall CAPTION. `loadProfile` now runs its OWN bounded retry loop
    // (see api/resilient-boot), so we no longer re-trigger it here — that would just no-op behind
    // the in-flight guard. This only surfaces the "taking a while" note while that loop works.
    later(() => setMinElapsed(true), MIN_MS);
    later(() => {
      if (statusRef.current === "ready") return; // already in — no note needed
      setStalled(true);
    }, STALL_MS);

    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [loadProfile, refreshFriends, refreshDmUnread]);

  const ready = status === "ready";
  const done = ready && minElapsed;

  // Deterministic creep to ~0.9 across the staged timeline; snap to 1 when done.
  const staged = 0.12 + stage * 0.26; // 0.12 · 0.38 · 0.64 · 0.90
  const progress = done ? 1 : Math.min(staged, ready ? 0.96 : 0.9);

  const caption =
    stalled && !ready ? fa.boot.slow : (STAGE_CAPTIONS[Math.min(stage, 3)] ?? fa.boot.preparing);

  return { done, caption, progress };
}

/** Await a promise but never longer than `ms` — a slow non-critical fetch must
 * not hold the splash. Swallows rejections (boot proceeds regardless). */
async function withTimeout(p: Promise<unknown>, ms: number): Promise<void> {
  await Promise.race([
    Promise.resolve(p).catch(() => undefined),
    new Promise<void>((resolve) => window.setTimeout(resolve, ms)),
  ]);
}
